import { useParams } from '@tanstack/react-router';
import { Ban, CalendarCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  FeatureErrorBoundary,
  useErrorMessage,
} from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { AdminAuctionSale } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ViewOnStorefrontButton } from '@/features/stores';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { AuctionsNav } from '../components/auctions-nav';
import { ReasonDialog } from '../components/reason-dialog';
import { SaleForm } from '../components/sale-form';
import { SaleLotsCard } from '../components/sale-lots-card';
import { SaleStatusBadge } from '../components/sale-status-badge';
import { useCancelSale, useScheduleSale } from '../hooks/use-sale-mutations';
import { useSale } from '../hooks/use-sales';
import { canCancelSale, canSchedule } from '../schemas/sale-rules';

const ROUTE = '/_authed/_active/s/$storeId/auctions/sales/$saleId' as const;

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="flex flex-col gap-0.5" title={title}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

function SaleSummary({ sale }: { sale: AdminAuctionSale }) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const interval =
    sale.lotIntervalSeconds === 0
      ? t('sales.summary.together')
      : t('sales.summary.interval', { count: Math.round(sale.lotIntervalSeconds / 60) });
  return (
    <Card data-testid="sale-summary">
      <CardContent className="grid gap-4 py-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t('sales.summary.lots')} value={String(sale.lotCount)} />
        <Stat
          label={t('sales.summary.sold')}
          value={sale.status === 'ended' ? String(sale.soldCount) : t('none')}
        />
        <Stat
          label={t('sales.summary.hammerTotal')}
          value={sale.hammerTotal.amount > 0 ? format.money(sale.hammerTotal) : t('none')}
        />
        <Stat
          label={t('sales.summary.opens')}
          value={format.dateTime(sale.startsAt)}
          title={format.utc(sale.startsAt)}
        />
        <Stat
          label={t('sales.summary.firstClose')}
          value={format.dateTime(sale.firstCloseAt)}
          title={format.utc(sale.firstCloseAt)}
        />
        <Stat label={t('sales.summary.then')} value={interval} />
      </CardContent>
    </Card>
  );
}

function SaleDetail() {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const message = useErrorMessage();
  const { saleId } = useParams({ from: ROUTE });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [cancelling, setCancelling] = useState(false);
  const query = useSale(store.id, saleId);
  const schedule = useScheduleSale(store.id);
  const cancel = useCancelSale(store.id);
  const sale = query.data;
  if (!sale) {
    return query.error ? (
      <PageContent>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContent>
    ) : null;
  }

  return (
    <PageContent>
      <PageHeader
        title={sale.title}
        meta={<SaleStatusBadge status={sale.status} />}
        description={
          <span title={format.utc(sale.updatedAt)}>
            <code>/sales/{sale.handle}</code>
            {' · '}
            {t('sales.updatedAt', { when: format.relative(sale.updatedAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/auctions/sales', params: { storeId: store.id } }}
        backLabel={t('sales.title')}
        actions={
          <>
            <ViewOnStorefrontButton
              url={sale.storefrontUrl}
              live={
                sale.status === 'scheduled' || sale.status === 'live' || sale.status === 'ended'
              }
            />
            {editable ? (
              <Can permission="auctions.write">
                {sale.status === 'draft' ? (
                  <Button
                    disabled={!canSchedule(sale) || schedule.isPending}
                    title={canSchedule(sale) ? undefined : t('sales.schedule.needsLots')}
                    onClick={() =>
                      schedule.mutate(sale.id, { onError: (error) => toast.error(message(error)) })
                    }
                    data-tour="auctions.sale.schedule"
                  >
                    <CalendarCheck aria-hidden />
                    {schedule.isPending ? t('actions.working') : t('sales.actions.schedule')}
                  </Button>
                ) : null}
                {canCancelSale(sale) ? (
                  <Button
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setCancelling(true)}
                  >
                    <Ban aria-hidden />
                    {t('sales.actions.cancel')}
                  </Button>
                ) : null}
              </Can>
            ) : null}
          </>
        }
      />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <AuctionsNav storeId={store.id} />
      <div className="flex flex-col gap-6">
        {sale.cancelReason ? (
          <p className="text-muted-foreground text-sm" role="status">
            {t('sales.cancelledReason', { reason: sale.cancelReason })}
          </p>
        ) : null}
        <SaleSummary sale={sale} />
        <SaleLotsCard storeId={store.id} sale={sale} canWrite={editable} />
        <SaleForm
          key={sale.updatedAt}
          storeId={store.id}
          sale={sale}
          timeZone={displayTimeZone(store.timezone)}
          canWrite={editable}
        />
      </div>
      {editable ? (
        <ReasonDialog
          open={cancelling}
          onOpenChange={(open) => {
            if (!open) setCancelling(false);
          }}
          title={t('sales.cancel.title')}
          description={t('sales.cancel.description', { count: sale.lotCount })}
          label={t('cancel.reason')}
          hint={t('cancel.hint')}
          confirmLabel={t('sales.cancel.confirm')}
          pending={cancel.isPending}
          onConfirm={(text) => cancel.mutateAsync({ id: sale.id, reason: text })}
        />
      ) : null}
    </PageContent>
  );
}

export function SaleDetailPage() {
  return (
    <FeatureErrorBoundary name="auctions.sale">
      <SaleDetail />
    </FeatureErrorBoundary>
  );
}
